<template>
    <div class="segment-search" ref="searchContainer">
        <el-popover :visible="showPopover"
                    placement="bottom-start"
                    :show-arrow="false"
                    :width="popoverWidth"
                    popper-class="search-results-popover"
                    :teleported="true">

            <div class="results-container"
                 v-loading="loading">
                <template v-if="!loading">
                    <template v-if="results.length > 0">
                        <SearchResultItem v-for="result in results"
                                          :key="result.translationSegmentId"
                                          :result="result"
                                          :searchText="searchText"
                                          :searchField="searchField"
                                          @select="handleResultSelect" />

                        <div v-if="hasMore" class="load-more">
                            <el-button @click="loadMore"
                                       :loading="loadingMore"
                                       type="primary"
                                       link>
                                {{ $t('segmentSearch.loadMore') }} ({{ remainingCount }})
                            </el-button>
                        </div>
                    </template>

                    <div v-else class="no-results">
                        {{ $t('segmentSearch.noResults') }}
                    </div>
                </template>
            </div>

            <template #reference>
                <el-input id="search-input"
                          v-model="searchText"
                          :placeholder="$t('segmentSearch.search')"
                          class="search-input"
                          clearable
                          @input="onSearchInput"
                          @focus="onFocus"
                          @clear="onClear">
                    <template #prefix>
                        <span class="material-symbols-outlined search-icon">search</span>
                    </template>
                </el-input>
            </template>
        </el-popover>
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';
    import { debounce } from 'lodash';

    import { documentStore } from '@/stores/documentStore';
    import { pagingStore } from '@/stores/pagingStore';
    import { segmentsStore } from '@/stores/segmentsStore';

    import { segmentSearch } from './segmentSearchService';
    import { SegmentSearchResultItem, SegmentSearchRequest, SearchField } from './SegmentSearchModels';
    import SearchResultItem from './SearchResultItem.vue';

    const PAGE_SIZE = 5;
    const DEBOUNCE_DELAY = 400;

    export default defineComponent({
        name: 'SegmentSearch',
        components: {
            SearchResultItem
        },
        props: {
            searchField: {
                type: Number as PropType<SearchField>,
                required: true
            }
        },
        data() {
            return {
                searchText: '',
                results: [] as SegmentSearchResultItem[],
                totalCount: 0,
                hasMore: false,
                currentPage: 1,
                loading: false,
                loadingMore: false,
                searchPerformed: false,
                popoverVisible: true,
                skipNextFocus: false,
                popoverWidth: 500,

                documentStore: documentStore(),
                pagingStore: pagingStore(),
                segmentsStore: segmentsStore(),
                debouncedSearch: null as ReturnType<typeof debounce> | null
            };
        },
        computed: {
            showPopover(): boolean {
                return this.searchPerformed && this.searchText.length > 0 && this.popoverVisible;
            },
            remainingCount(): number {
                return this.totalCount - this.results.length;
            }
        },
        watch: {
            searchField() {
                if (this.searchText) {
                    this.resetAndSearch();
                }
            }
        },
        mounted() {
            this.debouncedSearch = debounce(this.performSearch, DEBOUNCE_DELAY);
            this.updatePopoverWidth();
            window.addEventListener('resize', this.updatePopoverWidth);
            document.addEventListener('click', this.handleClickOutside);
        },
        beforeUnmount() {
            if (this.debouncedSearch) {
                this.debouncedSearch.cancel();
            }
            window.removeEventListener('resize', this.updatePopoverWidth);
            document.removeEventListener('click', this.handleClickOutside);
        },
        methods: {
            handleClickOutside(event: MouseEvent) {
                const searchContainer = this.$refs.searchContainer as HTMLElement;
                const popover = document.querySelector('.search-results-popover');

                if (!searchContainer || !this.popoverVisible) return;

                const clickedInContainer = searchContainer.contains(event.target as Node);
                const clickedInPopover = popover?.contains(event.target as Node);

                if (!clickedInContainer && !clickedInPopover) {
                    this.skipNextFocus = true;
                    this.popoverVisible = false;
                }
            },
            updatePopoverWidth() {
                const searchElement = this.$el?.querySelector('.search-input');
                if (searchElement) {
                    this.popoverWidth = searchElement.offsetWidth;
                }
            },
            onSearchInput() {
                this.popoverVisible = true;
                if (this.debouncedSearch) {
                    this.debouncedSearch();
                }
            },
            onFocus() {
                if (this.skipNextFocus) {
                    this.skipNextFocus = false;
                    return;
                }
                this.popoverVisible = true;
            },
            onClear() {
                this.resetSearch();
            },
            resetSearch() {
                this.results = [];
                this.totalCount = 0;
                this.hasMore = false;
                this.currentPage = 1;
                this.searchPerformed = false;
                this.popoverVisible = true;
            },
            resetAndSearch() {
                this.results = [];
                this.totalCount = 0;
                this.hasMore = false;
                this.currentPage = 1;
                this.performSearch();
            },
            async performSearch() {
                const searchText = this.searchText.trim();
                if (!searchText || searchText.length < 3) {
                    this.resetSearch();
                    return;
                }

                this.loading = true;
                this.searchPerformed = true;

                try {
                    const request: SegmentSearchRequest = {
                        documentId: this.documentStore.documentId,
                        searchText: searchText,
                        searchField: this.searchField,
                        page: 1,
                        pageSize: PAGE_SIZE,
                        displayPageSize: this.pagingStore.pageSize
                    };

                    const response = await segmentSearch(request);

                    this.results = response.results;
                    this.totalCount = response.totalCount;
                    this.hasMore = response.hasMore;
                    this.currentPage = 1;
                } catch (error) {
                    console.error('Search failed:', error);
                    this.results = [];
                    this.totalCount = 0;
                    this.hasMore = false;
                } finally {
                    this.loading = false;
                }
            },
            async loadMore() {
                if (this.loadingMore || !this.hasMore) return;

                this.loadingMore = true;

                try {
                    const request: SegmentSearchRequest = {
                        documentId: this.documentStore.documentId,
                        searchText: this.searchText.trim(),
                        searchField: this.searchField,
                        page: this.currentPage + 1,
                        pageSize: PAGE_SIZE,
                        displayPageSize: this.pagingStore.pageSize
                    };

                    const response = await segmentSearch(request);

                    this.results = [...this.results, ...response.results];
                    this.hasMore = response.hasMore;
                    this.currentPage++;
                } catch (error) {
                    console.error('Load more failed:', error);
                } finally {
                    this.loadingMore = false;
                }
            },
            async handleResultSelect(result: SegmentSearchResultItem) {
                this.skipNextFocus = true;
                this.popoverVisible = false;

                const targetPageNumber = result.displayPageNumber;
                const currentPageNumber = this.pagingStore.currentPageNumber;

                if (targetPageNumber !== currentPageNumber) {
                    this.pagingStore.goToPage(targetPageNumber);
                    await this.$nextTick();
                    setTimeout(() => {
                        this.focusSegment(result.translationSegmentId);
                    }, 200);
                } else {
                    this.focusSegment(result.translationSegmentId);
                }
            },
            calculatePageNumber(segmentNumber: number): number {
                const pageSize = this.pagingStore.pageSize;
                return Math.ceil(segmentNumber / pageSize);
            },
            focusSegment(segmentId: number): boolean {
                return this.segmentsStore.setCurrentSegmentId(segmentId);
            }
        }
    });
</script>

<style lang="scss" scoped>
    .search-input {
        :deep(.el-input__wrapper) {
            background-color: var(--light-blue);
            box-shadow: none;
            border-radius: 8px;
            padding: 12px 16px;
        }

        :deep(.el-input__inner) {
            color: var(--dark-blue);
            font-size: 16px;

            &::placeholder {
                color: var(--dark-blue);
                opacity: 0.7;
            }
        }

        :deep(.el-input__wrapper:hover) {
            box-shadow: none;
        }

        :deep(.el-input__wrapper.is-focus) {
            box-shadow: 0 0 0 2px rgba(52, 74, 109, 0.2);
        }
    }

    .search-icon {
        color: var(--dark-blue);
        opacity: 0.7;
    }

    .search-results-popover {
        padding: 0 !important;
        max-height: 400px;
        overflow-y: auto;

        .results-container {
            min-height: 60px;
            background-color: var(--merge-split);
        }

        .no-results {
            padding: 20px;
            text-align: center;
            color: var(--light-blue);
            opacity: 0.7;
        }

        .load-more {
            padding: 12px;
            text-align: center;
        }
    }

</style>

